import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Mock templates data
const mockTemplates = [
  {
    id: 1,
    name: 'Invoice Template',
    description: 'Standard invoice template with company branding',
    created_at: '2025-06-15T10:00:00Z',
    updated_at: '2025-06-18T14:30:00Z',
    uses_count: 45
  },
  {
    id: 2,
    name: 'Employee Letter Template',
    description: 'Template for employee correspondence and announcements',
    created_at: '2025-06-10T09:15:00Z',
    updated_at: '2025-06-19T11:20:00Z',
    uses_count: 23
  },
  {
    id: 3,
    name: 'Contract Agreement',
    description: 'Legal contract template with standard terms',
    created_at: '2025-06-05T16:45:00Z',
    updated_at: '2025-06-20T08:10:00Z',
    uses_count: 12
  },
  {
    id: 4,
    name: 'Marketing Flyer',
    description: 'Promotional material template for marketing campaigns',
    created_at: '2025-06-01T14:20:00Z',
    updated_at: '2025-06-15T13:45:00Z',
    uses_count: 67
  }
];

function Templates() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      setTemplates(mockTemplates);
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setTemplates(templates.filter(t => t.id !== templateId));
    } catch (err) {
      setError('Failed to delete template');
      console.error(err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-stone-50 to-amber-50">
      <nav className="bg-white/95 backdrop-blur-md shadow sticky top-0 z-50 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-2xl font-extrabold bg-gradient-to-r from-slate-800 via-amber-600 to-stone-700 bg-clip-text text-transparent">📄 Letterly</Link>
          <div className="hidden md:flex gap-6 text-slate-600 font-medium">
            <Link to="/dashboard"  className="hover:text-amber-600">Dashboard</Link>
            <Link to="/templates"  className="hover:text-amber-600">Templates</Link>
            <Link to="/jobs"       className="hover:text-amber-600">PDF Jobs</Link>
            <Link to="/upload"     className="hover:text-amber-600">Upload</Link>
          </div>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-slate-800">PDF Templates</h1>
          <Link
            to="/templates/new"
            className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-2 rounded-full shadow hover:scale-105 transition font-medium"
          >
            Create New Template
          </Link>
        </div>
        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {templates.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No templates</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first PDF template.
            </p>
            <div className="mt-6">
              <Link
                to="/templates/new"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
              >
                Create New Template
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div key={template.id} className="bg-white rounded-xl shadow p-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center mb-2">
                    <div className="h-10 w-10 rounded-lg bg-amber-500 flex items-center justify-center">
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-lg font-bold text-amber-700 truncate">{template.name}</p>
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          {template.paper_size || 'A4'}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center text-sm text-slate-500">
                        <p>Created: {formatDate(template.created_at)}</p>
                        {template.updated_at !== template.created_at && (
                          <p className="ml-4">Updated: {formatDate(template.updated_at)}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-slate-600 text-sm mb-4">{template.description}</div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Link
                    to={`/preview/${template.id}`}
                    className="bg-white border border-slate-300 text-slate-700 px-4 py-1.5 rounded-full shadow hover:bg-slate-100 font-medium text-sm"
                  >
                    Preview
                  </Link>
                  <Link
                    to={`/templates/${template.id}`}
                    className="bg-white border border-slate-300 text-slate-700 px-4 py-1.5 rounded-full shadow hover:bg-slate-100 font-medium text-sm"
                  >
                    Edit
                  </Link>
                  <Link
                    to={`/generate/${template.id}`}
                    className="bg-white border border-green-400 text-green-700 px-4 py-1.5 rounded-full shadow hover:bg-green-50 font-medium text-sm"
                  >
                    Generate PDFs
                  </Link>
                  <button
                    onClick={() => deleteTemplate(template.id)}
                    className="bg-white border border-red-300 text-red-600 px-4 py-1.5 rounded-full shadow hover:bg-red-50 font-medium text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Templates;
